<script setup>
import { ref, computed, h, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  createColumnHelper,
  useVueTable,
  getCoreRowModel,
} from '@tanstack/vue-table';
import Table from 'dashboard/components/table/Table.vue';
import UserAvatarWithName from 'dashboard/components/widgets/UserAvatarWithName.vue';
import { useAlert } from 'dashboard/composables';
import { useMapGetter, useStore } from 'dashboard/composables/store.js';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';

import WppConnectAPI from 'dashboard/routes/dashboard/settings/inbox/channels/WppConnectAPI';
import CommonWhatsappAPI from 'dashboard/routes/dashboard/settings/inbox/channels/commonWhatsapp/api';

const props = defineProps({
  isLoading: Boolean,
  inbox: { type: Object, default: () => {} },
});
const emit = defineEmits(['onSubmit', 'success']);

const { t } = useI18n();
const store = useStore();

const contacts = useMapGetter('contacts/getContactsList');
const meta = useMapGetter('contacts/getMeta');

const groupName = ref('');
const participants = ref([]);
const pageIndex = ref(1);
const pageSize = 15;

const totalItems = computed(() => meta.value?.count);

const currentPlayer = computed(() => {
  return props.inbox.player === 'codechat' ? CommonWhatsappAPI : WppConnectAPI;
});

const tableData = computed(() =>
  contacts.value.map(response => ({
    contact: response,
    phone_number: response.phoneNumber,
  }))
);
const handleSelection = (checked, phoneNumber) => {
  if (checked) {
    participants.value.push(phoneNumber);
  } else {
    participants.value = participants.value.filter(p => p !== phoneNumber);
  }
};

const defaultSpanRender = cellProps =>
  h(
    'span',
    {
      class: cellProps.getValue()
        ? 'capitalize text-n-slate-12'
        : 'capitalize text-n-slate-11',
    },
    cellProps.getValue() ? cellProps.getValue() : '---'
  );

const columnHelper = createColumnHelper();

const columns = [
  columnHelper.accessor('contact', {
    header: t('CONTACTS_PAGE.LIST.TABLE_HEADER.NAME'),
    cell: cellProps => {
      const contact = cellProps.getValue();
      return contact
        ? h(UserAvatarWithName, {
            user: contact,
            size: '24px',
            textClass: 'text-sm text-n-slate-12 max-w-[200px]',
          })
        : h('span', {}, '---');
    },
  }),
  columnHelper.accessor('phone_number', {
    header: t('CONTACTS_PAGE.LIST.TABLE_HEADER.PHONE_NUMBER'),
    cell: defaultSpanRender,
  }),
  columnHelper.accessor('add_phone', {
    header: () =>
      h('div', { class: 'text-center w-full' }, t('FORMS.MULTISELECT.SELECT')),
    cell: cellProps => {
      const row = cellProps.row.original;
      return h(
        'div',
        { class: 'flex justify-center' },
        h('input', {
          type: 'checkbox',
          class: 'checkbox',
          checked: participants.value.includes(row.phone_number),
          onChange: e => handleSelection(e.target.checked, row.phone_number),
        })
      );
    },
  }),
];

const table = useVueTable({
  get data() {
    return tableData.value;
  },
  columns,
  enableSorting: false,
  getCoreRowModel: getCoreRowModel(),
});

const createGroup = async () => {
  if (participants.value.length === 0 || !groupName.value.trim()) {
    useAlert(t('Necessário informar corretamente os campos'));
    return;
  }

  emit('onSubmit');

  const { data } = await currentPlayer.value.newGroup(
    props.inbox.phone_number,
    {
      operation: 'create',
      participants: participants.value,
      groupName: groupName.value,
      inboxId: props.inbox.id,
    }
  );

  if (!data || !data.success) {
    useAlert(t('Ocorreu algum problema ao criar o grupo'));
    return;
  }

  emit('success');
  useAlert(t('Sucesso ao criar o grupo'));
};

const getCommonFetchParams = (page = 1) => ({
  page,
  sortAttr: 'last_activity_at',
  // label: activeLabel.value,
});

const fetchContacts = async (page = 1) =>
  store.dispatch('contacts/get', getCommonFetchParams(page));

const onPageChange = newVal => {
  if (pageIndex.value !== newVal) {
    pageIndex.value = newVal;
    fetchContacts(newVal);
  }
};

onMounted(() => {
  fetchContacts();
});
</script>

<template>
  <div class="space-y-4">
    <Input
      v-model="groupName"
      :custom-input-class="[
        'h-9 w-full [&:not(.focus)]:!border-transparent bg-n-alpha-2 dark:bg-n-solid-1 !py-1',
      ]"
      type="text"
      :placeholder="$t('CHAT_LIST.GROUP_NAME')"
    />
    <div>
      <Table :table="table" />
      <PaginationFooter
        current-page-info="CONTACTS_LAYOUT.PAGINATION_FOOTER.SHOWING"
        :current-page="pageIndex"
        :total-items="totalItems"
        :items-per-page="pageSize"
        @update:current-page="onPageChange"
      />
      <div v-if="!tableData.length" class="text-center text-slate-400 mt-4">
        {{ $t('CONTACTS_PAGE.LIST.NO_CONTACTS') }}
      </div>
    </div>
    <div class="modal-footer flex justify-end">
      <NextButton
        :label="$t('CHAT_LIST.CREATE')"
        :disabled="!groupName || isLoading || participants.length === 0"
        :is-loading="isLoading"
        @click="createGroup"
      />
    </div>
  </div>
</template>
